/**
 * Column preset factories — shorthand builders that apply sensible defaults
 * for common column types, so callers don't have to remember the `dataType`
 * enum and can express intent inline.
 *
 * Every preset:
 *   1. Keeps all overrides the caller supplies (spread after the defaults)
 *   2. Falls back to type-based rendering when `cell` is omitted
 *   3. Requires at least `id` + `header` + an accessor/value source
 *
 * Example :
 *   moneyColumn<Product>({ id: 'price', header: 'Prix', accessor: 'priceEur' })
 */

import { cn } from '../../../lib/utils';
import type { DataGridColumn } from './types';

type RequiredBits = 'id' | 'header';
type PresetInput<T> = Omit<DataGridColumn<T>, 'dataType'> & Pick<DataGridColumn<T>, RequiredBits>;

/**
 * Money column — right-aligned, rendered as a localized currency string,
 * exported with `#,##0.00 "XXX"` numFmt. The `currency` override lets
 * callers mix MAD and EUR columns in the same grid when needed.
 */
export function moneyColumn<T>(
  col: PresetInput<T> & { currency?: string; precision?: number }
): DataGridColumn<T> {
  return {
    dataType: 'currency',
    ...col,
  };
}

/** Integer column (`#,##0`) — right-aligned, no decimals. */
export function integerColumn<T>(col: PresetInput<T>): DataGridColumn<T> {
  return {
    dataType: 'integer',
    ...col,
  };
}

/** Decimal number column (`#,##0.00`) — configurable precision. */
export function numberColumn<T>(col: PresetInput<T> & { precision?: number }): DataGridColumn<T> {
  return {
    dataType: 'number',
    ...col,
  };
}

/**
 * Percent column — expects values in `[0..1]` (not pre-multiplied by 100).
 * Renders as `45,00 %` and exports with Excel's native percent format.
 */
export function percentColumn<T>(
  col: PresetInput<T> & { precision?: number }
): DataGridColumn<T> {
  return {
    dataType: 'percent',
    ...col,
  };
}

/** Date-only column — `dd/mm/yyyy` display, `dd/mm/yyyy` Excel format. */
export function dateColumn<T>(col: PresetInput<T>): DataGridColumn<T> {
  return {
    dataType: 'date',
    ...col,
  };
}

/** Date + time column — `dd/mm/yyyy hh:mm` display and export. */
export function dateTimeColumn<T>(col: PresetInput<T>): DataGridColumn<T> {
  return {
    dataType: 'datetime',
    ...col,
  };
}

/**
 * Duration column — values are MINUTES. Display uses the `Xh YYm`
 * convention, Excel export uses `[h]:mm` (after conversion to day fractions).
 */
export function durationColumn<T>(col: PresetInput<T>): DataGridColumn<T> {
  return {
    dataType: 'duration',
    ...col,
  };
}

/** Boolean column — rendered as "Oui"/"Non", center-aligned. */
export function booleanColumn<T>(col: PresetInput<T>): DataGridColumn<T> {
  return {
    dataType: 'boolean',
    ...col,
  };
}

/** Plain text column — no special formatting, left-aligned (the default). */
export function textColumn<T>(col: PresetInput<T>): DataGridColumn<T> {
  return {
    dataType: 'text',
    ...col,
  };
}

/**
 * "Slot de présence" column — used for half-hour attendance matrices (ex :
 * planning US-C, ~40 colonnes de 30 min). There's no scalar value to
 * display, just a filled cell when the slot is occupied, so this preset
 * builds `cell` / `sortValue` / `exportValue` / `slotValue` directly
 * instead of going through the shared `dataType` renderer — `accessor`
 * returns a `boolean`, which the base `ColumnAccessor<T>` type doesn't
 * support.
 *
 * - On screen  : filled `bg-success-500` square when `accessor(row)` is true.
 * - In CSV/sort: `'●'` / `''` (via `exportValue` / `sortValue`).
 * - In Excel   : blank cell with a solid green fill when true (see
 *   `slotValue` + the Excel writer's dedicated `dataType: 'slot'` case) —
 *   no text, the fill alone carries the signal.
 *
 * `absentAccessor` (optional) marks a slot as covered by an absence period
 * (ex : US-C day matrix, red fill). "Present" always takes precedence over
 * "absent" when both are true for the same row/slot — the factual clock-in
 * trace wins over the declared absence (ex: an employee absent in the
 * morning who nonetheless badges in for that slot).
 *
 * Pass `headerRotate: true` to write the column header vertically in the
 * Excel export, which keeps 40 narrow slot columns from forcing the sheet
 * absurdly wide.
 */
export function slotColumn<T>(
  col: Omit<DataGridColumn<T>, 'dataType' | 'accessor'> & {
    accessor: (row: T) => boolean;
    absentAccessor?: (row: T) => boolean;
    width?: string;
  }
): DataGridColumn<T> {
  const { accessor, absentAccessor, width, headerClassName, cellClassName, headerRotate, ...rest } = col;
  const finalWidth = width ?? '32px';
  return {
    dataType: 'slot',
    align: 'center',
    width: finalWidth,
    maxWidth: finalWidth,
    headerRotate,
    slotValue: accessor,
    slotAbsentValue: absentAccessor,
    // Header vertical a l'ecran si `headerRotate: true` (aligne avec l'export
    // Excel). `writing-mode: vertical-rl` + `rotate-180` = texte lu bottom-up,
    // cohérent avec la convention Excel `textRotation: 90`.
    headerClassName: cn(
      '!px-0',
      headerRotate && '[writing-mode:vertical-rl] rotate-180 whitespace-nowrap py-2',
      headerClassName
    ),
    cellClassName: cn('!px-0', cellClassName),
    cell: (row) => {
      const present = accessor(row);
      const absent = !present && (absentAccessor?.(row) ?? false);
      return (
        <span
          className={cn(
            'block h-full min-h-[1.5rem] w-full',
            present && 'bg-success-500',
            absent && 'bg-danger-500'
          )}
        />
      );
    },
    sortValue: (row) => (accessor(row) ? '●' : ''),
    exportValue: (row) => (accessor(row) ? '●' : ''),
    ...rest,
  };
}
